import Model from '@/store/plugins/orm/Model'
import Money from '@/wrappers/Money';
import { PriceCalculationItem } from './ModelRepository';
import i18n from '@/i18n';

export class PriceCalculation extends Model {
    static get entity () {
        return 'price_calculations'
    }

    static fields () {
        return {
            id: this.attr(null),
            number: this.attr(null),
            date: this.attr(null),
            user_id: this.attr(null),
            client_id: this.attr(null),
            invoice_number: this.attr(null),
            invoice_date: this.attr(null),
            invoice_due_date: this.attr(null),
            invoice_paid_date: this.attr(null),
            invoice_price_total: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            invoice_with_tax: this.attr(true, value => !!value),
            dependent_costs_total: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            description: this.attr(null),
            recorded_at: this.attr(null),
            items: this.attr([], value => value.map(item => new PriceCalculationItem(item))),

            client: this.attr(null),
        }
    }

    getInvoicePriceTotal() {
        return Money.parse(this.invoice_price_total);
    }

    getDependentCostsTotal() {
        return Money.parse(this.dependent_costs_total);
    }

    getInvoiceValue() {
        return this.items.reduce((acc, cur) => acc.add(cur.getInvoiceValue()), Money.parse(0));
    }

    getDependentCosts() {
        return this.items.reduce((acc, cur) => acc.add(cur.getDependentCosts()), Money.parse(0));
    }

    getPurchaseValue() {
        return this.items.reduce((acc, cur) => acc.add(cur.getPurchaseValue()), Money.parse(0));
    }

    getPurchaseTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getPurchaseTax()), Money.parse(0));
    }

    getPurchaseValueAfterTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getPurchaseValueAfterTax()), Money.parse(0));
    }

    getPriceDifference() {
        return this.items.reduce((acc, cur) => acc.add(cur.getPriceDifference()), Money.parse(0));
    }

    getSellingValue() {
        return this.items.reduce((acc, cur) => acc.add(cur.getSellingValue()), Money.parse(0));
    }

    getTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0));
    }

    getTaxesPerRate() {
        const taxes = {};

        this.items.forEach(item => {
            if(taxes[item.tax_rate]) {
                taxes[item.tax_rate].value = taxes[item.tax_rate].value.add(item.getTax());
                taxes[item.tax_rate].priceBeforeTax = taxes[item.tax_rate].priceBeforeTax.add(item.getSellingValue());
            } else {
                taxes[item.tax_rate] = {
                    value: item.getTax(),
                    priceBeforeTax: item.getSellingValue(),
                }
            }
        });

        return Object.keys(taxes).map(rate => {
            return {
                rate: rate,
                value: taxes[rate].value,
                priceBeforeTax: taxes[rate].priceBeforeTax,
            }
        })
    }

    getSellingValueAfterTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getSellingValueAfterTax()), Money.parse(0));
    }

    toTextLines() {
        const venue = this.$store().getters.tenant?.venue;
        if(!venue) return [];

        const lines = [];

        lines.push(`[HR]`);
        lines.push(`[C][H22]${i18n.t('price_calculations.heading')}`);
        lines.push(`[HR]`);
        lines.push(`[L]`);
        lines.push(`[L]${i18n.t('price_calculations.number')}: ${this.number}[R]${venue.name}`);
        lines.push(`[L]${i18n.t('price_calculations.date')}: ${moment(this.date).format('DD.MM.YYYY.')}[R]${i18n.t('general.tax_id_number')}: ${venue.tax_id_number}`);

        if(this.description) {
            lines.push(`[L]${i18n.t('price_calculations.description')}: ${this.description}`);
        }

        lines.push(`[L]`);
        lines.push(`[L]`);
        lines.push(`[L]`);
        lines.push(`[L]`);

        const leftSummaryColumns = [
            `${i18n.tc('general.supplier', 1)}: ${this.client?.name || ''}`,
            `${i18n.t('price_calculations.invoice_number')}: &nbsp${this.invoice_number}`,
            `${i18n.t('price_calculations.invoice_date')}: &nbsp${moment(this.invoice_date).format('DD.MM.YYYY.')}`
        ];

        if(this.invoice_due_date) {
            leftSummaryColumns.push(`${i18n.t('price_calculations.invoice_due_date')}:&nbsp ${moment(this.invoice_due_date).format('DD.MM.YYYY.')}`);
        }

        if(this.invoice_paid_date) {
            leftSummaryColumns.push(`${i18n.t('price_calculations.invoice_paid_date')}: &nbsp${moment(this.invoice_paid_date).format('DD.MM.YYYY.')}`);
        }

        leftSummaryColumns.push(`${i18n.t('price_calculations.invoice_value')}: &nbsp${this.getInvoiceValue().toFormat('0.00')}`);
        leftSummaryColumns.push(`${i18n.t('price_calculations.dependent_costs')}: &nbsp${this.getDependentCosts().toFormat('0.00')}`);

        const rightSummaryColumns = [
            `${i18n.t('price_calculations.purchase_value')}: &nbsp${this.getPurchaseValue().toFormat('0.00')}`,
            `${i18n.t('price_calculations.purchase_tax')}: &nbsp${this.getPurchaseTax().toFormat('0.00')}`,
            `${i18n.t('price_calculations.purchase_value_after_tax')}:&nbsp ${this.getPurchaseValueAfterTax().toFormat('0.00')}`,
            `${i18n.t('price_calculations.price_difference')}: &nbsp${this.getPriceDifference().toFormat('0.00')}`,
            `${i18n.t('price_calculations.selling_value')}: &nbsp${this.getSellingValue().toFormat('0.00')}`,
            `${i18n.t('price_calculations.tax')}: &nbsp${this.getTax().toFormat('0.00')}`,
            `${i18n.t('price_calculations.selling_value_after_tax')}: &nbsp${this.getSellingValueAfterTax().toFormat('0.00')}`,
        ];

        lines.push(`[L][H22]${i18n.t('price_calculations.section_supplier_invoice')}[R]${i18n.t('price_calculations.section_summary')}`);
        lines.push(`[HR]`);
        lines.push(`[L]`);

        for(let i = 0; i < rightSummaryColumns.length; i++) {
            lines.push(`[L]${leftSummaryColumns[i] || ''}[R]${rightSummaryColumns[i]}`)
        }

        lines.push(`[L]`);
        lines.push(`[L]`);
        lines.push(`[L]`);
        lines.push(`[L]`);

        lines.push(`[HR]`);
        lines.push(
            `[L][H00]${i18n.t('general.number')}` +
            `[L]${i18n.t('general.code')}` +
            `[L]${i18n.t('general.inventory_item')}` +
            `[R]${i18n.t('price_calculations.quantity')}` +
            `[R]${i18n.t('price_calculations.invoice_price')}` +
            `[R]${i18n.t('price_calculations.invoice_discount_percent')}` +
            `[R]${i18n.t('price_calculations.invoice_price_after_discount')}` +
            `[R]${i18n.t('price_calculations.invoice_value')}` +
            `[R]${i18n.t('price_calculations.dependent_costs')}` +
            `[R]${i18n.t('price_calculations.purchase_value')}` +
            `[R]${i18n.t('price_calculations.price_difference')}` +
            `[R]${i18n.t('price_calculations.selling_value')}` +
            `[R]${i18n.t('price_calculations.tax')}` +
            `[R]${i18n.t('price_calculations.selling_value_after_tax')}` +
            `[R]${i18n.t('price_calculations.purchase_price')}` +
            `[R]${i18n.t('price_calculations.margin_percent')}` +
            `[R]${i18n.t('price_calculations.price_difference_per_unit')}` +
            `[R]${i18n.t('price_calculations.selling_price')}` +
            `[R]${i18n.t('price_calculations.selling_price_after_tax')}`
        );
        lines.push(`[HR]`);

        this.items.forEach((item, index) => {
            lines.push(
                `[L][H00]${index + 1}` +
                `[L]${item.getProduct() && item.getProduct().code ? item.getProduct().code : ''}` +
                `[L]${item.getProduct() ? item.getProduct().name : ''}` +
                `[R]&nbsp${item.getQuantity()} ${item.getUnitOfMeasure()}` +
                `[R]&nbsp${item.getInvoicePrice().toFormat('0.00')}` +
                `[R]&nbsp${item.getInvoiceDiscountPercent()}` +
                `[R]&nbsp${item.getInvoicePriceAfterDiscount().toFormat('0.00')}` +
                `[R]&nbsp${item.getInvoiceValue().toFormat('0.00')}` +
                `[R]&nbsp${item.getDependentCosts().toFormat('0.00')}` +
                `[R]&nbsp${item.getPurchaseValue().toFormat('0.00')}` +
                `[R]&nbsp${item.getPriceDifference().toFormat('0.00')}` +
                `[R]&nbsp${item.getSellingValue().toFormat('0.00')}` +
                `[R]&nbsp${item.getTax().toFormat('0.00')} (${item.getTaxRate()}%)` +
                `[R]&nbsp${item.getSellingValueAfterTax().toFormat('0.00')}` +
                `[R]&nbsp${item.getPurchasePrice().toFormat('0.00')}` +
                `[R]&nbsp${item.getMarginPercent()}` +
                `[R]&nbsp${item.getPriceDifferencePerUnit().toFormat('0.00')}` +
                `[R]&nbsp${item.getSellingPrice().toFormat('0.00')}` +
                `[R]&nbsp${item.getSellingPriceAfterTax().toFormat('0.00')}`
            );
        })

        lines.push(`[HR]`);
        lines.push(`[L]`);
        lines.push(`[L]`);

        // lines.push(
        //     `[L][H00][L][L][L][L][L]` +
        //     `[R]${i18n.t('general.total').toUpperCase()}:` +
        //     `[R]${this.getInvoiceValue().toFormat('0.00')}` +
        //     `[R]${this.getDependentCosts().toFormat('0.00')}` +
        //     `[R]${this.getPurchaseValue().toFormat('0.00')}` +
        //     `[R]${this.getPriceDifference().toFormat('0.00')}` +
        //     `[R]${this.getSellingValue().toFormat('0.00')}` +
        //     `[R]${this.getTax().toFormat('0.00')}` +
        //     `[R]${this.getSellingValueAfterTax().toFormat('0.00')}` +
        //     `[L][L][L][L][L]`
        // );

        this.getTaxesPerRate().forEach(item => {
            lines.push(
                `[L][H00][L][L][L][L][L][L][L][L][L][L][L]` +
                `[L]${i18n.t('price_calculations.prica_before_tax')} ${parseFloat(item.rate)}%` +
                `[R]${item.priceBeforeTax.toFormat('0.00')}`
            );
            lines.push(
                `[L][H00][L][L][L][L][L][L][L][L][L][L][L]` +
                `[L]${i18n.t('price_calculations.tax_rate')} ${parseFloat(item.rate)}%` +
                `[R]${item.value.toFormat('0.00')}`
            );
        })

        lines.push(`[L]`);
        lines.push(`[L]`);
        lines.push(`[L]`);
        lines.push(`[L]`);
        lines.push(`[L]`);
        lines.push(`[L]`);

        lines.push(`[L][C]${i18n.t('general.created_by')}[L][C]${i18n.t('general.received_by')}[L][C]${i18n.t('general.responsible_person')}[L]`);
        lines.push(`[L]`);
        lines.push(`[L][C]________________[L][C]________________[L][C]_________________[L]`);

        return lines;
    }
}

export default PriceCalculation
